import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Form,
  Button,
  Row,
  Col,
  Card,
  Tabs,
  Tab,
  Table,
} from "react-bootstrap";
import moment from "moment";
import { toast } from "react-toastify";
import { getAllEventTypes } from "../Api/Event";
import Select from "react-select";

import {
  fetchEventByCode,
  fetchParticipantsByEvent,
  fetchRemindersByEvent,
  fetchMoMByEvent,
} from "../Api/ViewEvent";

import {
  updateEvent,
  addParticipants,
  addReminder,
  addMoM,
} from "../Api/Event";

import "./EditEvent.css";

const EditEvent = () => {
  const { eventCode } = useParams();

  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const orgId = userDetails?.organisationId;
  const userId = userDetails?.userId;

  const [eventData, setEventData] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);

  const [participants, setParticipants] = useState([]);
  const [existingParticipants, setExistingParticipants] = useState([]);

  const [reminderMinutes, setReminderMinutes] = useState("");
  const [reminderType, setReminderType] = useState("");
  const [isSent, setIsSent] = useState(false);
  const [existingReminders, setExistingReminders] = useState([]);

  const [existingMoM, setExistingMoM] = useState(null);

  // ✅ NEW MoM STRUCTURE
  const [momMeta, setMomMeta] = useState({
    preparedBy: "",
    approvedBy: "",
  });

  const [momDetails, setMomDetails] = useState([
    {
      discussionPoint: "",
      decisionTaken: "",
      actionItem: "",
    },
  ]);

  // ================= LOAD =================

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = () => {
    loadEvent();
    loadParticipants();
    loadReminders();
    loadMoM();
    loadEventTypes();
  };

  const loadEvent = async () => {
    try {
      const data = await fetchEventByCode(orgId, eventCode);

      setEventData({
        eventName: data.eventName,
        eventType: data.eventType,
        eventDate: moment(data.eventDate).format("YYYY-MM-DD"),
        eventEndDate: data.eventEndDate
          ? moment(data.eventEndDate).format("YYYY-MM-DD")
          : "",
        eventLocation: data.eventLocation,
        organizerName: data.organizerName,
        budgetAllocation: data.budgetAllocation,
        department: data.department,
        eventDescription: data.eventDescription,
        entityType: data.entityType,
        entityId: data.entityId,
      });
    } catch {
      toast.error("Error loading event");
    }
  };

  const loadParticipants = async () => {
    try {
      const data = await fetchParticipantsByEvent(eventCode);
      setExistingParticipants(data);
    } catch {
      toast.error("Error loading participants");
    }
  };

  const loadReminders = async () => {
    try {
      const data = await fetchRemindersByEvent(eventCode);
      setExistingReminders(data);
    } catch {
      toast.error("Error loading reminders");
    }
  };

  const loadMoM = async () => {
    try {
      const data = await fetchMoMByEvent(eventCode);

      setExistingMoM(data);

      // ✅ Fill Prepared & Approved
      setMomMeta({
        preparedBy: data?.preparedBy || "",
        approvedBy: data?.approvedBy || "",
      });

      // ✅ Fill rows
      if (data?.details?.length > 0) {
        setMomDetails(
          data.details.map((d) => ({
            discussionPoint: d.discussionPoint || "",
            decisionTaken: d.decisionTaken || "",
            actionItem: d.actionItem || "",
          })),
        );
      }
    } catch {
      toast.error("Error loading MoM");
    }
  };

  const loadEventTypes = async () => {
    try {
      const data = await getAllEventTypes();
      setEventTypes(data);
    } catch {
      toast.error("Error loading event types");
    }
  };

  // ================= HANDLERS =================

  const handleChange = (e) => {
    setEventData({
      ...eventData,
      [e.target.name]: e.target.value,
    });
  };

  const handleOverviewSave = async () => {
    try {
      const payload = {
        ...eventData,
        eventDate: new Date(eventData.eventDate).toISOString(),
        eventEndDate: eventData.eventEndDate
          ? new Date(eventData.eventEndDate).toISOString()
          : null,
        createdBy: String(userId),
        organisationId: String(orgId),
      };

      await updateEvent(eventCode, payload);
      toast.success("Event Updated Successfully");
    } catch {
      toast.error("Failed to update event");
    }
  };

  const handleAddParticipant = async () => {
    try {
      const payload = participants.map((p) => ({
        ...p,
        createdBy: String(userId),
        organisationId: String(orgId),
      }));

      await addParticipants(eventCode, payload);

      toast.success("Participants Added");
      setParticipants([]);
      loadParticipants();
    } catch {
      toast.error("Error adding participants");
    }
  };

  const handleAddReminder = async () => {
    try {
      const payload = {
        reminderMinutes,
        reminderType,
        isSent: isSent === "true",
        createdBy: String(userId),
        organisationId: String(orgId),
      };

      await addReminder(eventCode, payload);

      toast.success("Reminder Saved");

      setReminderMinutes("");
      setReminderType("");
      setIsSent(false);

      loadReminders();
    } catch {
      toast.error("Error saving reminder");
    }
  };

  // ✅ UPDATED MoM SAVE
  const handleSaveMoM = async () => {
    try {
      const payload = {
        preparedBy: momMeta.preparedBy,
        approvedBy: momMeta.approvedBy,
        discussionPoints: momDetails.map((x) => x.discussionPoint),
        decisionsTaken: momDetails.map((x) => x.decisionTaken),
        actionItems: momDetails.map((x) => x.actionItem),
        createdBy: String(userId),
        organisationId: String(orgId),
      };

      await addMoM(eventCode, payload);

      toast.success("MoM Saved");
      loadMoM();
    } catch {
      toast.error("Error saving MoM");
    }
  };

  const eventTypeOptions = eventTypes.map((e) => ({
    value: e.eventTypeGuid,
    label: e.typeName,
  }));

  if (!eventData) return null;

  return (
    <div className="main-content expand">
      <Container fluid className="fields-container">
        <h2 className="fields-title">
          {eventData.eventName} ({eventCode})
        </h2>

        <Tabs defaultActiveKey="overview">
          {/* ================= OVERVIEW ================= */}

          <Tab eventKey="overview" title="📋 Overview">
            <Card className="mt-3">
              <Card.Body>
                <Form>
                  <Row>
                    <Col md={4}>
                      <Form.Label>Event Name</Form.Label>
                      <Form.Control
                        name="eventName"
                        value={eventData.eventName}
                        onChange={handleChange}
                      />
                    </Col>

                    <Col md={4}>
                      <Form.Label>Event Type</Form.Label>
                      <Select
                        options={eventTypeOptions}
                        value={eventTypeOptions.find(
                          (opt) => opt.value === eventData.eventType,
                        )}
                        onChange={(selected) =>
                          setEventData({
                            ...eventData,
                            eventType: selected.value,
                          })
                        }
                      />
                    </Col>

                    <Col md={4}>
                      <Form.Label>Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="eventDate"
                        value={eventData.eventDate}
                        onChange={handleChange}
                      />
                    </Col>
                  </Row>

                  <Button className="mt-3" onClick={handleOverviewSave}>
                    Update Event
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          </Tab>

          {/* ================= PARTICIPANTS ================= */}

          <Tab eventKey="participants" title="👥 Participants">
            <Card className="mt-3">
              <Card.Body>
                {participants.map((p, i) => (
                  <Row key={i} className="mb-2">
                    <Col md={4}>
                      <Form.Control
                        placeholder="User Id"
                        onChange={(e) => {
                          const arr = [...participants];
                          arr[i].userId = e.target.value;
                          setParticipants(arr);
                        }}
                      />
                    </Col>

                    <Col md={4}>
                      <Form.Control
                        placeholder="Role"
                        onChange={(e) => {
                          const arr = [...participants];
                          arr[i].participantRole = e.target.value;
                          setParticipants(arr);
                        }}
                      />
                    </Col>

                    <Col md={4}>
                      <Form.Select
                        onChange={(e) => {
                          const arr = [...participants];
                          arr[i].status = e.target.value;
                          setParticipants(arr);
                        }}
                      >
                        <option value="">Status</option>
                        <option value="Accepted">Accepted</option>
                        <option value="Pending">Pending</option>
                        <option value="Declined">Declined</option>
                      </Form.Select>
                    </Col>
                  </Row>
                ))}

                <Button
                  onClick={() =>
                    setParticipants([
                      ...participants,
                      { userId: "", participantRole: "", status: "" },
                    ])
                  }
                >
                  Add Participant
                </Button>

                <Button className="ms-2" onClick={handleAddParticipant}>
                  Save
                </Button>

                <hr />

                <Table bordered>
                  <tbody>
                    {existingParticipants.map((p, i) => (
                      <tr key={i}>
                        <td>{p.userId}</td>
                        <td>{p.participantRole}</td>
                        <td>{p.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>

          {/* ================= REMINDERS ================= */}

          <Tab eventKey="reminders" title="⏰ Reminders">
            <Card className="mt-3">
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <Form.Control
                      placeholder="Minutes"
                      value={reminderMinutes}
                      onChange={(e) => setReminderMinutes(e.target.value)}
                    />
                  </Col>

                  <Col md={4}>
                    <Form.Select
                      value={reminderType}
                      onChange={(e) => setReminderType(e.target.value)}
                    >
                      <option value="">Type</option>
                      <option value="Email">Email</option>
                      <option value="Notification">Notification</option>
                    </Form.Select>
                  </Col>

                  <Col md={4}>
                    <Form.Select
                      value={isSent}
                      onChange={(e) => setIsSent(e.target.value)}
                    >
                      <option value="">Sent?</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </Form.Select>
                  </Col>
                </Row>

                <Button className="mt-2" onClick={handleAddReminder}>
                  Save Reminder
                </Button>

                <Table className="mt-3">
                  <tbody>
                    {existingReminders.map((r, i) => (
                      <tr key={i}>
                        <td>{r.reminderMinutes}</td>
                        <td>{r.reminderType}</td>
                        <td>{r.isSent ? "Yes" : "No"}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>

          {/* ================= MOM ================= */}

          <Tab eventKey="mom" title="📝 MoM">
            <Card className="mt-3">
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Control
                      placeholder="Prepared By"
                      value={momMeta.preparedBy}
                      onChange={(e) =>
                        setMomMeta({
                          ...momMeta,
                          preparedBy: e.target.value,
                        })
                      }
                    />
                  </Col>

                  <Col md={6}>
                    <Form.Control
                      placeholder="Approved By"
                      value={momMeta.approvedBy}
                      onChange={(e) =>
                        setMomMeta({
                          ...momMeta,
                          approvedBy: e.target.value,
                        })
                      }
                    />
                  </Col>
                </Row>

                {momDetails.map((item, i) => (
                  <Row key={i} className="mt-2">
                    <Col md={4}>
                      <Form.Control
                        placeholder="Discussion"
                        value={item.discussionPoint}
                        onChange={(e) => {
                          const arr = [...momDetails];
                          arr[i].discussionPoint = e.target.value;
                          setMomDetails(arr);
                        }}
                      />
                    </Col>

                    <Col md={4}>
                      <Form.Control
                        placeholder="Decision"
                        value={item.decisionTaken}
                        onChange={(e) => {
                          const arr = [...momDetails];
                          arr[i].decisionTaken = e.target.value;
                          setMomDetails(arr);
                        }}
                      />
                    </Col>

                    <Col md={4}>
                      <Form.Control
                        placeholder="Action"
                        value={item.actionItem}
                        onChange={(e) => {
                          const arr = [...momDetails];
                          arr[i].actionItem = e.target.value;
                          setMomDetails(arr);
                        }}
                      />
                    </Col>
                  </Row>
                ))}

                <Button
                  className="mt-2"
                  onClick={() =>
                    setMomDetails([
                      ...momDetails,
                      {
                        discussionPoint: "",
                        decisionTaken: "",
                        actionItem: "",
                      },
                    ])
                  }
                >
                  Add Row
                </Button>

                <Button className="mt-2 ms-2" onClick={handleSaveMoM}>
                  Save MoM
                </Button>

                <hr />

                <Table bordered>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Discussion</th>
                      <th>Decision</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {existingMoM?.details?.map((d, i) => (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td>{d.discussionPoint}</td>
                        <td>{d.decisionTaken}</td>
                        <td>{d.actionItem}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>
      </Container>
    </div>
  );
};

export default EditEvent;

// import { useEffect, useState } from "react";
// import { useParams } from "react-router-dom";
// import {
//   Container,
//   Form,
//   Button,
//   Row,
//   Col,
//   Card,
//   Tabs,
//   Tab,
//   Table,
// } from "react-bootstrap";
// import moment from "moment";
// import { toast } from "react-toastify";
// import { getAllEventTypes } from "../Api/Event";
// import Select from "react-select";
// import {
//   fetchEventByCode,
//   fetchParticipantsByEvent,
//   fetchRemindersByEvent,
//   fetchMoMByEvent,
// } from "../Api/ViewEvent";

// import {
//   updateEvent,
//   addParticipants,
//   addReminder,
//   addMoM,
// } from "../Api/Event";

// import "./EditEvent.css";

// const EditEvent = () => {
//   const { eventCode } = useParams();

//   const userDetails = JSON.parse(localStorage.getItem("userDetails"));
//   const orgId = userDetails?.organisationId;
//   const userId = userDetails?.userId;

//   const [eventData, setEventData] = useState(null);

//   const [reminderType, setReminderType] = useState("");
//   const [isSent, setIsSent] = useState(false);

//   const [existingReminders, setExistingReminders] = useState([]);
//   const [existingMoM, setExistingMoM] = useState(null);

//   const [existingParticipants, setExistingParticipants] = useState([]);

//   const [eventTypes, setEventTypes] = useState([]);

//   const [participants, setParticipants] = useState([]);
//   const [reminderMinutes, setReminderMinutes] = useState("");
//   const [mom, setMom] = useState({
//     discussionPoints: "",
//     decisionsTaken: "",
//     actionItems: "",
//   });

//   useEffect(() => {
//     loadEvent();
//   }, []);

//   const loadEvent = async () => {
//     try {
//       const data = await fetchEventByCode(orgId, eventCode);

//       setEventData({
//         eventName: data.eventName,
//         eventType: data.eventType,
//         eventDate: moment(data.eventDate).format("YYYY-MM-DD"),
//         eventEndDate: data.eventEndDate
//           ? moment(data.eventEndDate).format("YYYY-MM-DD")
//           : "",
//         eventLocation: data.eventLocation,
//         organizerName: data.organizerName,
//         budgetAllocation: data.budgetAllocation,
//         department: data.department,
//         eventDescription: data.eventDescription,
//         entityType: data.entityType,
//         entityId: data.entityId,
//       });
//     } catch (error) {
//       toast.error("Error loading event");
//     }
//   };

//   const handleChange = (e) => {
//     setEventData({
//       ...eventData,
//       [e.target.name]: e.target.value,
//     });
//   };

//   const handleOverviewSave = async () => {
//     try {
//       const payload = {
//         ...eventData,
//         eventDate: new Date(eventData.eventDate).toISOString(),
//         eventEndDate: eventData.eventEndDate
//           ? new Date(eventData.eventEndDate).toISOString()
//           : null,
//         createdBy: String(userId),
//         organisationId: String(orgId),
//       };

//       await updateEvent(eventCode, payload);

//       toast.success("Event Updated Successfully");
//     } catch (error) {
//       toast.error("Failed to update event");
//     }
//   };

//   const handleAddParticipant = async () => {
//     try {
//       const payload = participants.map((p) => ({
//         ...p,
//         createdBy: String(userId),
//         organisationId: String(orgId),
//       }));

//       await addParticipants(eventCode, payload);

//       toast.success("Participants Added");

//       setParticipants([]);

//       loadParticipants();
//     } catch (error) {
//       toast.error("Error adding participants");
//     }
//   };

//   const handleAddReminder = async () => {
//     try {
//       const payload = {
//         reminderMinutes: reminderMinutes,
//         reminderType: reminderType,
//         isSent: isSent === "true",
//         createdBy: String(userId),
//         organisationId: String(orgId),
//       };

//       await addReminder(eventCode, payload);

//       toast.success("Reminder Saved");

//       setReminderMinutes("");
//       setReminderType("");
//       setIsSent(false);

//       loadReminders();
//     } catch {
//       toast.error("Error saving reminder");
//     }
//   };

//   const handleSaveMoM = async () => {
//     try {
//       const payload = {
//         ...mom,
//         createdBy: String(userId),
//         organisationId: String(orgId),
//       };

//       await addMoM(eventCode, payload);

//       toast.success("Minutes of Meeting Saved");

//       loadMoM();
//     } catch {
//       toast.error("Error saving MoM");
//     }
//   };

//   const loadParticipants = async () => {
//     try {
//       const data = await fetchParticipantsByEvent(eventCode);
//       setExistingParticipants(data);
//     } catch (error) {
//       toast.error("Error loading participants");
//     }
//   };

//   const loadReminders = async () => {
//     try {
//       const data = await fetchRemindersByEvent(eventCode);
//       setExistingReminders(data);
//     } catch {
//       toast.error("Error loading reminders");
//     }
//   };

//   const loadMoM = async () => {
//     try {
//       const data = await fetchMoMByEvent(eventCode);
//       setExistingMoM(data);
//     } catch {
//       toast.error("Error loading MoM");
//     }
//   };

//   useEffect(() => {
//     loadEvent();
//     loadParticipants();
//     loadReminders();
//     loadMoM();
//     loadEventTypes(); // ✅ ADD THIS
//   }, []);

//   const loadEventTypes = async () => {
//     try {
//       const data = await getAllEventTypes();
//       setEventTypes(data);
//     } catch {
//       toast.error("Error loading event types");
//     }
//   };

//   const eventTypeOptions = eventTypes.map((e) => ({
//     value: e.eventTypeGuid,
//     label: e.typeName,
//   }));

//   if (!eventData) return null;

//   return (
//     <div className="main-content expand">
//       <Container fluid className="fields-container">
//         <h2 className="fields-title">
//           {eventData?.eventName} ({eventCode})
//         </h2>
//         <Tabs defaultActiveKey="overview" className="event-tabs">
//           {/* OVERVIEW TAB */}

//           <Tab eventKey="overview" title="📋 Overview">
//             <Card className="mt-3">
//               <Card.Body>
//                 <Form className="fields-form">
//                   <Row>
//                     <Col md={4}>
//                       <Form.Group className="mb-3">
//                         <Form.Label>Event Name</Form.Label>
//                         <Form.Control
//                           name="eventName"
//                           value={eventData.eventName}
//                           onChange={handleChange}
//                         />
//                       </Form.Group>
//                     </Col>

//                     <Col md={4}>
//                       <Form.Group className="mb-3">
//                         <Form.Label>Event Type</Form.Label>
//                         <Select
//                           options={eventTypeOptions}
//                           value={eventTypeOptions.find(
//                             (opt) => opt.value === eventData.eventType,
//                           )}
//                           onChange={(selected) =>
//                             setEventData({
//                               ...eventData,
//                               eventType: selected.value, // ✅ store GUID
//                             })
//                           }
//                           placeholder="Select Event Type"
//                           menuPortalTarget={document.body}
//                           styles={{
//                             menuPortal: (base) => ({ ...base, zIndex: 9999 }),
//                           }}
//                         />
//                       </Form.Group>
//                     </Col>

//                     <Col md={4}>
//                       <Form.Group className="mb-3">
//                         <Form.Label>Event Date</Form.Label>
//                         <Form.Control
//                           type="date"
//                           name="eventDate"
//                           value={eventData.eventDate}
//                           onChange={handleChange}
//                         />
//                       </Form.Group>
//                     </Col>
//                   </Row>

//                   <Row>
//                     <Col md={4}>
//                       <Form.Group className="mb-3">
//                         <Form.Label>Event End Date</Form.Label>
//                         <Form.Control
//                           type="date"
//                           name="eventEndDate"
//                           value={eventData.eventEndDate}
//                           onChange={handleChange}
//                         />
//                       </Form.Group>
//                     </Col>

//                     <Col md={4}>
//                       <Form.Group className="mb-3">
//                         <Form.Label>Event Location</Form.Label>
//                         <Form.Control
//                           name="eventLocation"
//                           value={eventData.eventLocation}
//                           onChange={handleChange}
//                         />
//                       </Form.Group>
//                     </Col>

//                     <Col md={4}>
//                       <Form.Group className="mb-3">
//                         <Form.Label>Organizer</Form.Label>
//                         <Form.Control
//                           name="organizerName"
//                           value={eventData.organizerName}
//                           onChange={handleChange}
//                         />
//                       </Form.Group>
//                     </Col>
//                   </Row>

//                   <Row>
//                     <Col md={4}>
//                       <Form.Group className="mb-3">
//                         <Form.Label>Budget Allocation</Form.Label>
//                         <Form.Control
//                           type="number"
//                           name="budgetAllocation"
//                           value={eventData.budgetAllocation}
//                           onChange={handleChange}
//                         />
//                       </Form.Group>
//                     </Col>

//                     <Col md={4}>
//                       <Form.Group className="mb-3">
//                         <Form.Label>Department</Form.Label>
//                         <Form.Control
//                           name="department"
//                           value={eventData.department}
//                           onChange={handleChange}
//                         />
//                       </Form.Group>
//                     </Col>
//                   </Row>

//                   <Row>
//                     <Col md={12}>
//                       <Form.Group className="mb-3">
//                         <Form.Label>Description</Form.Label>
//                         <Form.Control
//                           as="textarea"
//                           rows={4}
//                           name="eventDescription"
//                           value={eventData.eventDescription}
//                           onChange={handleChange}
//                         />
//                       </Form.Group>
//                     </Col>
//                   </Row>

//                   <Row>
//                     <Col md={4}>
//                       <Form.Group className="mb-3">
//                         <Form.Label>Entity Type</Form.Label>
//                         <Form.Select
//                           name="entityType"
//                           value={eventData.entityType}
//                           onChange={handleChange}
//                         >
//                           <option value="">Select</option>
//                           <option value="Ticket">Ticket</option>
//                           <option value="Project">Project</option>
//                           <option value="Task">Task</option>
//                         </Form.Select>
//                       </Form.Group>
//                     </Col>

//                     <Col md={4}>
//                       <Form.Group className="mb-3">
//                         <Form.Label>Entity Id</Form.Label>
//                         <Form.Control
//                           name="entityId"
//                           value={eventData.entityId}
//                           onChange={handleChange}
//                         />
//                       </Form.Group>
//                     </Col>
//                   </Row>

//                   <div className="fields-btn-container">
//                     <Button
//                       variant="primary"
//                       className="fields-submit"
//                       onClick={handleOverviewSave}
//                     >
//                       Update Event
//                     </Button>
//                   </div>
//                 </Form>
//               </Card.Body>
//             </Card>
//           </Tab>

//           {/* PARTICIPANTS */}

//           <Tab eventKey="participants" title="👥 Participants">
//             <Card className="mt-3">
//               <Card.Body>
//                 <Form className="fields-form">
//                   {participants.map((p, i) => (
//                     <Row key={i} className="mb-3">
//                       <Col md={4}>
//                         <Form.Group>
//                           <Form.Label>User Id</Form.Label>
//                           <Form.Control
//                             value={p.userId}
//                             onChange={(e) => {
//                               const arr = [...participants];
//                               arr[i].userId = e.target.value;
//                               setParticipants(arr);
//                             }}
//                           />
//                         </Form.Group>
//                       </Col>

//                       <Col md={4}>
//                         <Form.Group>
//                           <Form.Label>Participant Role</Form.Label>
//                           <Form.Control
//                             value={p.participantRole}
//                             onChange={(e) => {
//                               const arr = [...participants];
//                               arr[i].participantRole = e.target.value;
//                               setParticipants(arr);
//                             }}
//                           />
//                         </Form.Group>
//                       </Col>

//                       <Col md={4}>
//                         <Form.Group>
//                           <Form.Label>Status</Form.Label>
//                           <Form.Select
//                             value={p.status}
//                             onChange={(e) => {
//                               const arr = [...participants];
//                               arr[i].status = e.target.value;
//                               setParticipants(arr);
//                             }}
//                           >
//                             <option value="">Select</option>
//                             <option value="Accepted">Accepted</option>
//                             <option value="Pending">Pending</option>
//                             <option value="Declined">Declined</option>
//                           </Form.Select>
//                         </Form.Group>
//                       </Col>
//                     </Row>
//                   ))}

//                   <div className="fields-btn-container">
//                     <Button
//                       variant="secondary"
//                       onClick={() =>
//                         setParticipants([
//                           ...participants,
//                           { userId: "", participantRole: "", status: "" },
//                         ])
//                       }
//                     >
//                       Add Participant
//                     </Button>

//                     <Button
//                       variant="primary"
//                       className="fields-submit ms-2"
//                       onClick={handleAddParticipant}
//                     >
//                       Save Participants
//                     </Button>
//                   </div>
//                 </Form>

//                 {/* EXISTING PARTICIPANTS TABLE */}

//                 {existingParticipants.length > 0 && (
//                   <>
//                     <hr />

//                     <h5>Existing Participants</h5>

//                     <Table bordered hover>
//                       <thead>
//                         <tr>
//                           <th>#</th>
//                           <th>User Id</th>
//                           <th>Role</th>
//                           <th>Status</th>
//                           <th>Created By</th>
//                           <th>Date</th>
//                         </tr>
//                       </thead>

//                       <tbody>
//                         {existingParticipants.map((p, index) => (
//                           <tr key={p.participantGuid}>
//                             <td>{index + 1}</td>
//                             <td>{p.userId}</td>
//                             <td>{p.participantRole}</td>
//                             <td>{p.status}</td>
//                             <td>{p.createdBy}</td>
//                             <td>
//                               {moment(p.createdOnDate).format("DD-MM-YYYY")}
//                             </td>
//                           </tr>
//                         ))}
//                       </tbody>
//                     </Table>
//                   </>
//                 )}
//               </Card.Body>
//             </Card>
//           </Tab>

//           {/* REMINDERS */}

//           <Tab eventKey="reminders" title="⏰ Reminders">
//             <Card className="mt-3">
//               <Card.Body>
//                 <Form className="fields-form">
//                   <Row>
//                     <Col md={4}>
//                       <Form.Group className="mb-3">
//                         <Form.Label>Reminder Minutes</Form.Label>
//                         <Form.Control
//                           type="number"
//                           value={reminderMinutes}
//                           onChange={(e) => setReminderMinutes(e.target.value)}
//                         />
//                       </Form.Group>
//                     </Col>

//                     <Col md={4}>
//                       <Form.Group className="mb-3">
//                         <Form.Label>Reminder Type</Form.Label>
//                         <Form.Select
//                           value={reminderType}
//                           onChange={(e) => setReminderType(e.target.value)}
//                         >
//                           <option value="">Select</option>
//                           <option value="Email">Email</option>
//                           <option value="Notification">Notification</option>
//                           <option value="SMS">SMS</option>
//                         </Form.Select>
//                       </Form.Group>
//                     </Col>

//                     <Col md={4}>
//                       <Form.Group className="mb-3">
//                         <Form.Label>Is Sent</Form.Label>
//                         <Form.Select
//                           value={isSent}
//                           onChange={(e) => setIsSent(e.target.value)}
//                         >
//                           <option value="">Select</option>
//                           <option value="true">Yes</option>
//                           <option value="false">No</option>
//                         </Form.Select>
//                       </Form.Group>
//                     </Col>
//                   </Row>

//                   <div className="fields-btn-container">
//                     <Button
//                       variant="primary"
//                       className="fields-submit"
//                       onClick={handleAddReminder}
//                     >
//                       Save Reminder
//                     </Button>
//                   </div>
//                 </Form>
//                 {existingReminders.length > 0 && (
//                   <>
//                     <hr />
//                     <h5>Existing Reminders</h5>

//                     <Table bordered hover>
//                       <thead>
//                         <tr>
//                           <th>#</th>
//                           <th>Minutes</th>
//                           <th>Type</th>
//                           <th>Sent</th>
//                           <th>Created By</th>
//                           <th>Date</th>
//                         </tr>
//                       </thead>

//                       <tbody>
//                         {existingReminders.map((r, index) => (
//                           <tr key={r.reminderGuid}>
//                             <td>{index + 1}</td>
//                             <td>{r.reminderMinutes}</td>
//                             <td>{r.reminderType}</td>
//                             <td>{r.isSent ? "Yes" : "No"}</td>
//                             <td>{r.createdBy}</td>
//                             <td>
//                               {moment(r.createdOnDate).format("DD-MM-YYYY")}
//                             </td>
//                           </tr>
//                         ))}
//                       </tbody>
//                     </Table>
//                   </>
//                 )}
//               </Card.Body>
//             </Card>
//           </Tab>

//           {/* MOM */}

//           <Tab eventKey="mom" title="📝 MoM">
//             <Card className="mt-3">
//               <Card.Body>
//                 <Form className="fields-form">
//                   <Row>
//                     <Col md={6}>
//                       <Form.Group className="mb-3">
//                         <Form.Label>Prepared By</Form.Label>
//                         <Form.Control
//                           onChange={(e) =>
//                             setMom({ ...mom, preparedBy: e.target.value })
//                           }
//                         />
//                       </Form.Group>
//                     </Col>

//                     <Col md={6}>
//                       <Form.Group className="mb-3">
//                         <Form.Label>Approved By</Form.Label>
//                         <Form.Control
//                           onChange={(e) =>
//                             setMom({ ...mom, approvedBy: e.target.value })
//                           }
//                         />
//                       </Form.Group>
//                     </Col>
//                   </Row>

//                   <Row>
//                     <Col md={12}>
//                       <Form.Group className="mb-3">
//                         <Form.Label>Discussion Points</Form.Label>
//                         <Form.Control
//                           as="textarea"
//                           rows={3}
//                           onChange={(e) =>
//                             setMom({ ...mom, discussionPoints: e.target.value })
//                           }
//                         />
//                       </Form.Group>
//                     </Col>
//                   </Row>

//                   <Row>
//                     <Col md={12}>
//                       <Form.Group className="mb-3">
//                         <Form.Label>Decisions Taken</Form.Label>
//                         <Form.Control
//                           as="textarea"
//                           rows={3}
//                           onChange={(e) =>
//                             setMom({ ...mom, decisionsTaken: e.target.value })
//                           }
//                         />
//                       </Form.Group>
//                     </Col>
//                   </Row>

//                   <Row>
//                     <Col md={12}>
//                       <Form.Group className="mb-3">
//                         <Form.Label>Action Items</Form.Label>
//                         <Form.Control
//                           as="textarea"
//                           rows={3}
//                           onChange={(e) =>
//                             setMom({ ...mom, actionItems: e.target.value })
//                           }
//                         />
//                       </Form.Group>
//                     </Col>
//                   </Row>

//                   <div className="fields-btn-container">
//                     <Button
//                       variant="primary"
//                       className="fields-submit"
//                       onClick={handleSaveMoM}
//                     >
//                       Save MoM
//                     </Button>
//                   </div>
//                 </Form>
//                 {existingMoM?.Details?.length > 0 && (
//                   <>
//                     <hr />
//                     <h5>Saved MoM</h5>

//                     <Table bordered>
//                       <thead>
//                         <tr>
//                           <th>#</th>
//                           <th>Discussion</th>
//                           <th>Decision</th>
//                           <th>Action</th>
//                         </tr>
//                       </thead>

//                       <tbody>
//                         {existingMoM.Details.map((d, i) => (
//                           <tr key={d.moMdetailGuid}>
//                             <td>{i + 1}</td>
//                             <td>{d.discussionPoint}</td>
//                             <td>{d.decisionTaken}</td>
//                             <td>{d.actionItem}</td>
//                           </tr>
//                         ))}
//                       </tbody>
//                     </Table>
//                   </>
//                 )}
//               </Card.Body>
//             </Card>
//           </Tab>
//         </Tabs>
//       </Container>
//     </div>
//   );
// };

// export default EditEvent;
